import {createApi} from '@reduxjs/toolkit/query/react';
import {customAxiosBaseQuery} from '../../API/customAxiosBaseQuery';
import {
	ApproveOrderRequestType,
	BaseResponseType,
	CreateInvoiceType,
	ListResponseType,
	OrderHistoryType,
	OrdersType,
	RequestOrdersListType,
	ResponseEditOrderType,
	ResponsePrintOrderType,
	ResponseProductListInDepotType,
	ResponseReceiveListType,
} from '../../types/TypesStore';
import {orderAPI, storeActivitiesAPI, storeOrderAPI} from '../../API/api';
import {
	ProductsListRequestType,
	ResponseCategoriesListType,
} from '../../types/ProductSliceTypes';

import {
	ResponseUserOrderServiceType,
	StoreOrderInfoResponseType,
} from '../../types/TypesUserOrders';
import {
	OrderInPaperworkType,
	RequestPaperworkListType,
	ResponsePaperworkListType,
} from '../../types/TypesDepotPaperwork';

export const depotOrdersApi = createApi({
	reducerPath: 'depotOrdersApi',
	baseQuery: customAxiosBaseQuery,
	tagTypes: [
		'OrdersList',
		'ProductsList',
		'CategoriesList',
		'OrderServiceList',
	],
	endpoints: (builder) => ({
		getProductsList: builder.query<
			ResponseProductListInDepotType,
			ProductsListRequestType
		>({
			query: (props) => ({
				func: storeOrderAPI.getProductsList,
				args: [props],
			}),
			providesTags: ['ProductsList'],
		}),
		getCategoriesList: builder.query<ResponseCategoriesListType, string>({
			query: (props) => ({
				func: storeOrderAPI.getCategoriesList,
				args: [props],
			}),
			providesTags: ['CategoriesList'],
		}),
		getOrderServiceList: builder.query<ResponseUserOrderServiceType, string>({
			query: (country) => ({
				func: orderAPI.getOrderService,
				args: [country],
			}),
			providesTags: ['OrderServiceList'],
		}),
		/*getOrdersList: builder.query<ListResponseType, RequestOrdersListType>({
			query: (props) => ({
				func: storeOrderAPI.getOrderList,
				args: [props],
			}),
			providesTags: ['OrdersList'],
		}),*/
		getOrdersList: builder.query<ListResponseType, RequestOrdersListType>({
			query: (props) => ({
				func: storeOrderAPI.getOrderList,
				args: [props],
			}),
			serializeQueryArgs: ({endpointName, queryArgs}) => {
				const {offset, ...filters} = (queryArgs ||
					{}) as RequestPaperworkListType;
				return `${endpointName}:${JSON.stringify(filters)}`;
			},
			merge: (currentCache, newItems) => {
				const prevOrders = currentCache.orders ?? [];
				const newOrders = newItems.orders ?? [];
				// объединяем заказы уникально по id
				const map = new Map<number, OrdersType>();
				for (const o of prevOrders) map.set(o.id, o);
				for (const o of newOrders) map.set(o.id, o);
				currentCache.orders = Array.from(map.values());

				// корректно суммируем агрегаты
				const prevCount = Number(currentCache.count_orders) || 0;
				const prevPoints = Number(currentCache.sum_points) || 0;
				const prevMoney = Number(currentCache.sum_money) || 0;

				const newCount = Number(newItems.count_orders) || 0;
				const newPoints = Number(newItems.sum_points) || 0;
				const newMoney = Number(newItems.sum_money) || 0;
				currentCache.count_orders = prevCount + newCount;
				currentCache.sum_points = (prevPoints + newPoints).toFixed(2);
				currentCache.sum_money = (prevMoney + newMoney).toFixed(2);
			},

			forceRefetch({currentArg, previousArg}) {
				return (
					currentArg?.offset !== previousArg?.offset ||
					JSON.stringify(currentArg) !== JSON.stringify(previousArg)
				);
			},

			providesTags: ['OrdersList'],
		}),
		getOrdersHistory: builder.query<
			BaseResponseType & {actions: OrderHistoryType[]},
			RequestOrdersListType
		>({
			query: (props) => ({
				func: storeOrderAPI.getOrderHistory,
				args: [props],
			}),
			providesTags: ['OrdersList'],
		}),
		getOrderView: builder.query<any, number>({
			query: (props) => ({
				func: storeOrderAPI.getOrderInfo,
				args: [props],
			}),
			providesTags: ['OrdersList'],
		}),
		getStoreOrderInfo: builder.query<StoreOrderInfoResponseType, number>({
			query: (order_id) => ({
				func: storeOrderAPI.getStoreOrderInfo,
				args: [order_id],
			}),
			providesTags: ['OrdersList'],
		}),
		editOrder: builder.query<ResponseEditOrderType, number>({
			query: (props) => ({
				func: storeOrderAPI.getEditOrder,
				args: [props],
			}),
		}),
		createInvoice: builder.mutation<any, CreateInvoiceType>({
			query: (props) => ({
				func: storeOrderAPI.getCreateInvoice,
				args: [props],
			}),
			invalidatesTags: ['OrdersList'],
		}),
		printOrder: builder.query<ResponsePrintOrderType, number>({
			query: (props) => ({
				func: storeOrderAPI.getPrintOrder,
				args: [props],
			}),
		}),
		deleteOrder: builder.mutation<void, number>({
			query: (props) => ({
				func: storeOrderAPI.getDeleteOrder,
				args: [props],
			}),
			invalidatesTags: ['OrdersList'],
		}),
		approveOrder: builder.mutation<void, ApproveOrderRequestType>({
			query: (props) => ({
				func: storeOrderAPI.getApproveOrder,
				args: [props],
			}),
			invalidatesTags: ['OrdersList'],
		}),
		getReceiveList: builder.query<ResponseReceiveListType, number>({
			query: (props) => ({
				func: storeOrderAPI.getReceiveList,
				args: [props],
			}),
		}),
	}),
});

export const {
	useGetOrdersListQuery,
	useGetOrdersHistoryQuery,
	useLazyPrintOrderQuery,
	useLazyGetOrderViewQuery,
	useLazyGetStoreOrderInfoQuery,
	useDeleteOrderMutation,
	useApproveOrderMutation,
	useLazyEditOrderQuery,
	useLazyGetReceiveListQuery,
	useGetCategoriesListQuery,
	useGetProductsListQuery,
	useGetOrderServiceListQuery,
	useCreateInvoiceMutation,
} = depotOrdersApi;
